import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SEO from "@/components/SEO";
import ImageUploader from "@/components/ImageUploader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, ShieldCheck, TrendingUp, Handshake, ArrowRight, Plus, Pencil, Trash2, ExternalLink, Mail } from "lucide-react";

// -------------------- Landing --------------------
export function AgenceLanding() {
  return (
    <>
      <SEO title="Espace agences immobilières — venteaterme.fr" description="Diffusez vos mandats en vente à terme sur venteaterme.fr. 100 % gratuit, sans commission, dashboard dédié." />
      <section className="pt-24 pb-14 bg-navy text-cream">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.3em] text-accent-brand font-bold mb-4 flex items-center gap-2"><Building2 size={14} /> Agences</div>
          <h1 className="font-serif text-4xl md:text-6xl leading-tight">Un espace dédié aux professionnels.</h1>
          <p className="text-cream/85 mt-6 max-w-2xl text-lg">
            Diffusez vos mandats en vente à terme, gérez vos annonces et recevez les demandes qualifiées de nos visiteurs.
          </p>
          <p className="text-cream mt-4 max-w-2xl font-semibold">
            <span className="inline-block bg-accent-brand text-white text-xs uppercase tracking-widest font-bold px-3 py-1.5 mr-3">100&nbsp;% gratuit</span>
            Pas d'abonnement, pas de commission au lancement.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Link to="/agences/inscription" data-testid="ag-cta-signup" className="inline-flex items-center gap-2 bg-accent-brand text-white px-8 py-3 font-bold tracking-wide hover:bg-[#B08D22] transition-colors">
              Créer un compte agence <ArrowRight size={18} />
            </Link>
            <Link to="/agences/connexion" className="inline-flex items-center gap-2 border-2 border-cream/40 text-cream px-8 py-3 font-bold tracking-wide hover:bg-cream hover:text-navy transition-colors">
              Se connecter
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-10 grid md:grid-cols-3 gap-6">
          {[
            { icon: TrendingUp, title: "Diffusion large", text: "Vos annonces sont référencées sur les pages villes, guides SEO et le fil principal." },
            { icon: Handshake, title: "Leads qualifiés", text: "Les demandes de renseignement arrivent directement dans votre tableau de bord." },
            { icon: ShieldCheck, title: "Modération sérieuse", text: "Chaque annonce est validée sous 48 h par notre équipe pour maintenir la qualité." },
          ].map((c, i) => (
            <div key={i} className="p-8 border border-navy/10 bg-white">
              <c.icon className="text-accent-brand mb-4" size={36} strokeWidth={1.4} />
              <h3 className="font-serif text-xl text-navy mb-2">{c.title}</h3>
              <p className="text-sm text-navy/70">{c.text}</p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

// -------------------- Register --------------------
export function AgenceRegister() {
  const { user, agencyRegister } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "", company: "", siret: "", contact_name: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (user?.role === "agency") return <Navigate to="/agences/tableau-de-bord" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 8) { setErr("Mot de passe : 8 caractères minimum."); return; }
    setBusy(true);
    const res = await agencyRegister(f);
    setBusy(false);
    if (res.ok) { nav("/agences/tableau-de-bord"); return; }
    setErr(res.error || "Erreur d'inscription");
  };

  const set = (k, v) => setF({ ...f, [k]: v });

  return (
    <>
      <SEO title="Créer un compte agence — venteaterme.fr" description="Inscription gratuite pour les agences immobilières." />
      <section className="min-h-[80vh] py-24 px-6 flex items-center justify-center">
        <form onSubmit={submit} data-testid="agency-register-form" className="w-full max-w-lg bg-white border border-navy/10 p-10 space-y-4">
          <div className="text-xs uppercase tracking-[0.3em] text-accent-brand font-bold mb-2">Agences</div>
          <h1 className="font-serif text-3xl text-navy mb-2">Créer un compte agence</h1>
          <p className="text-xs text-navy/60 mb-4">Inscription gratuite. Vos annonces sont modérées avant publication.</p>
          <div><Label>Nom de l'agence</Label><Input required data-testid="ag-reg-company" value={f.company} onChange={(e) => set("company", e.target.value)} className="mt-2 border-navy/20" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>SIRET</Label><Input value={f.siret} onChange={(e) => set("siret", e.target.value)} className="mt-2 border-navy/20" /></div>
            <div><Label>Téléphone</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} className="mt-2 border-navy/20" /></div>
          </div>
          <div><Label>Nom du contact</Label><Input value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="mt-2 border-navy/20" /></div>
          <div><Label>Email professionnel</Label><Input required type="email" data-testid="ag-reg-email" value={f.email} onChange={(e) => set("email", e.target.value)} className="mt-2 border-navy/20" /></div>
          <div><Label>Mot de passe (8 car. min)</Label><Input required type="password" data-testid="ag-reg-password" value={f.password} onChange={(e) => set("password", e.target.value)} className="mt-2 border-navy/20" /></div>
          {err && <div data-testid="ag-reg-error" className="text-sm text-destructive">{err}</div>}
          <button disabled={busy} data-testid="ag-reg-submit" className="w-full bg-accent-brand text-white py-3 font-bold tracking-wide hover:bg-[#B08D22] disabled:opacity-60">
            {busy ? "Création..." : "Créer mon compte"}
          </button>
          <div className="text-xs text-navy/60 pt-2 border-t border-navy/10">Déjà un compte ? <Link to="/agences/connexion" className="text-accent-brand underline">Se connecter</Link></div>
        </form>
      </section>
    </>
  );
}

// -------------------- Login --------------------
export function AgenceLogin() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [needs2fa, setNeeds2fa] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user?.role === "agency") return <Navigate to="/agences/tableau-de-bord" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    const res = await login(email, password, totp);
    setBusy(false);
    if (res.ok) { nav("/agences/tableau-de-bord"); return; }
    if (res.requires2fa) { setNeeds2fa(true); return; }
    setErr(res.error);
  };

  return (
    <>
      <SEO title="Connexion agence — venteaterme.fr" description="Espace agences immobilières." />
      <section className="min-h-[70vh] flex items-center justify-center py-24 px-6">
        <form onSubmit={submit} data-testid="agency-login-form" className="w-full max-w-md bg-white border border-navy/10 p-10">
          <div className="text-xs uppercase tracking-[0.3em] text-accent-brand font-bold mb-3">Agences</div>
          <h1 className="font-serif text-3xl text-navy mb-8">Connexion agence</h1>
          <div className="space-y-4">
            <div><Label>Email</Label><Input required type="email" data-testid="ag-login-email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 border-navy/20" /></div>
            <div><Label>Mot de passe</Label><Input required type="password" data-testid="ag-login-password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 border-navy/20" /></div>
            {needs2fa && (
              <div><Label>Code à 6 chiffres</Label><Input required data-testid="ag-login-totp" value={totp} onChange={(e) => setTotp(e.target.value)} className="mt-2 border-navy/20 tracking-widest" placeholder="123456" /></div>
            )}
            {err && <div data-testid="ag-login-error" className="text-sm text-destructive">{err}</div>}
            <button disabled={busy} data-testid="ag-login-submit" className="w-full bg-accent-brand text-white py-3 font-bold tracking-wide hover:bg-[#B08D22] disabled:opacity-60">
              {busy ? "Connexion..." : "Se connecter"}
            </button>
            <div className="text-xs text-navy/60 pt-2 border-t border-navy/10">Pas encore de compte ? <Link to="/agences/inscription" className="text-accent-brand underline">S'inscrire</Link></div>
          </div>
        </form>
      </section>
    </>
  );
}

// -------------------- Dashboard --------------------
const EMPTY = {
  title: "", city: "", region: "", postal_code: "", property_type: "libre",
  surface: 60, rooms: 3, bedrooms: 2, market_value: 250000, bouquet: 50000,
  duration_years: 15, description: "", images: [], dpe_energy: "", dpe_climate: "",
  dpe_kwh: "", dpe_costs: "",
};

function StatusBadge({ s }) {
  return (
    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 ${s === "approved" ? "bg-accent-brand text-white" : s === "rejected" ? "bg-red-600 text-white" : "bg-navy/10 text-navy"}`}>
      {s === "approved" ? "Publiée" : s === "rejected" ? "Refusée" : "En modération"}
    </span>
  );
}

export function AgenceDashboard() {
  const { user, logout } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [listings, setListings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // {id, kind:'listing'|'submission', data}
  const [f, setF] = useState(EMPTY);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    const [{ data: mine }, { data: inqs }] = await Promise.all([
      api.get("/agency/listings").catch(() => ({ data: { submissions: [], listings: [] } })),
      api.get("/agency/inquiries").catch(() => ({ data: [] })),
    ]);
    setSubmissions(mine.submissions || []);
    setListings(mine.listings || []);
    setInquiries(inqs || []);
  };

  useEffect(() => { reload(); }, []);

  if (!user) return <Navigate to="/agences/connexion" replace />;
  if (user.role !== "agency") return <Navigate to="/agences/connexion" replace />;

  const startCreate = () => { setEditing(null); setF(EMPTY); setPhotos([]); setShowForm(true); };
  const startEdit = (l, kind) => {
    setEditing({ id: l.id, kind });
    setF({ ...EMPTY, ...l });
    setPhotos((l.images || []).map((u) => ({ id: u.split("/").pop().replace(/\?.*$/, ""), url: u })));
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditing(null); };

  const backendBase = api.defaults.baseURL.replace(/\/api$/, "");

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const image_urls = photos.map((p) => p.url && p.url.startsWith("http") ? p.url : `${backendBase}/api/uploads/${p.id}`);
      const payload = { ...f, images: image_urls };
      ["surface","rooms","bedrooms","market_value","bouquet","duration_years"].forEach((k) => { payload[k] = Number(payload[k]); });
      if (editing) {
        await api.put(`/agency/listings/${editing.id}`, payload);
        toast.success("Annonce mise à jour.");
      } else {
        await api.post("/agency/listings", payload);
        toast.success("Annonce envoyée pour modération.");
      }
      closeForm();
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur d'enregistrement"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement cette annonce ?")) return;
    try { await api.delete(`/agency/listings/${id}`); toast.success("Supprimée."); reload(); }
    catch { toast.error("Erreur de suppression"); }
  };

  return (
    <>
      <SEO title="Tableau de bord agence — venteaterme.fr" description="Gérez vos annonces et leads en vente à terme." />
      <section className="pt-24 pb-8 bg-navy text-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-accent-brand font-bold mb-2">Agence</div>
            <h1 className="font-serif text-3xl md:text-4xl">{user.company || user.email}</h1>
            <p className="text-cream/60 text-sm mt-1">{user.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="ag-new-listing" onClick={startCreate} className="inline-flex items-center gap-2 bg-accent-brand text-white px-5 py-2.5 font-bold tracking-wide hover:bg-[#B08D22] transition-colors">
              <Plus size={16} /> Nouvelle annonce
            </button>
            <button data-testid="ag-logout" onClick={logout} className="text-sm text-cream/70 hover:text-accent-brand">Se déconnecter</button>
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-10">
          <div>
            <h2 className="font-serif text-2xl text-navy mb-4">Mes annonces publiées ({listings.length})</h2>
            <div className="space-y-3">
              {listings.length === 0 && <div className="text-sm text-navy/50 border border-dashed border-navy/20 p-8 text-center">Aucune annonce publiée pour le moment.</div>}
              {listings.map((l) => (
                <div key={l.id} data-testid={`ag-listing-${l.id}`} className="bg-white border border-navy/10 p-4 flex items-start justify-between gap-4">
                  <div>
                    <StatusBadge s="approved" />
                    <h3 className="font-serif text-navy mt-2">{l.title}</h3>
                    <div className="text-xs text-navy/60 mt-1">{l.city} · {l.surface} m² · {l.market_value?.toLocaleString("fr-FR")} €</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Link to={`/annonces/${l.id}`} className="p-2 text-navy/60 hover:text-accent-brand" aria-label="Voir en ligne"><ExternalLink size={16} /></Link>
                    <button onClick={() => startEdit(l, "listing")} className="p-2 text-navy/60 hover:text-accent-brand" aria-label="Modifier"><Pencil size={16} /></button>
                    <button onClick={() => remove(l.id)} className="p-2 text-red-600 hover:bg-red-50" aria-label="Supprimer"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>

            <h2 className="font-serif text-2xl text-navy mt-10 mb-4">En attente de modération ({submissions.filter((s) => s.status === "pending").length})</h2>
            <div className="space-y-3">
              {submissions.filter((s) => s.status !== "approved").map((s) => (
                <div key={s.id} data-testid={`ag-sub-${s.id}`} className="bg-white border border-navy/10 p-4 flex items-start justify-between gap-4">
                  <div>
                    <StatusBadge s={s.status} />
                    <h3 className="font-serif text-navy mt-2">{s.title || s.description?.slice(0, 60) || "Annonce"}</h3>
                    <div className="text-xs text-navy/60 mt-1">{s.city} · {s.surface} m² · {s.market_value?.toLocaleString("fr-FR")} €</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => startEdit(s, "submission")} className="p-2 text-navy/60 hover:text-accent-brand" aria-label="Modifier"><Pencil size={16} /></button>
                    <button onClick={() => remove(s.id)} className="p-2 text-red-600 hover:bg-red-50" aria-label="Supprimer"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
              {submissions.filter((s) => s.status !== "approved").length === 0 && <div className="text-sm text-navy/50">Rien en attente.</div>}
            </div>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-navy mb-4 flex items-center gap-2"><Mail size={20} /> Demandes reçues ({inquiries.length})</h2>
            <div className="space-y-3">
              {inquiries.length === 0 && <div className="text-sm text-navy/50 border border-dashed border-navy/20 p-8 text-center">Aucune demande pour l'instant.</div>}
              {inquiries.map((i) => (
                <div key={i.id} className="bg-white border border-navy/10 p-4">
                  <div className="text-xs text-navy/50">{new Date(i.created_at).toLocaleDateString("fr-FR")}</div>
                  <div className="font-serif text-navy mt-1">{i.name} — {i.email}</div>
                  <div className="text-xs text-navy/60 mt-1">📞 {i.phone || "n/c"} · Sur : {i.listing_title}</div>
                  {i.message && <p className="text-sm text-navy/75 mt-2 border-t border-navy/10 pt-2">{i.message}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {showForm && (
        <div className="fixed inset-0 bg-navy/70 z-50 flex items-start justify-center p-6 overflow-y-auto">
          <form onSubmit={save} data-testid="ag-listing-form" className="bg-white w-full max-w-2xl p-8 my-10 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl text-navy">{editing ? "Modifier l'annonce" : "Nouvelle annonce"}</h2>
              <button type="button" onClick={closeForm} className="text-navy/50 hover:text-navy">✕</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Titre</Label><Input required data-testid="ag-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1 border-navy/20" /></div>
              <div><Label>Ville</Label><Input required data-testid="ag-city" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className="mt-1 border-navy/20" /></div>
              <div><Label>Code postal</Label><Input value={f.postal_code} onChange={(e) => setF({ ...f, postal_code: e.target.value })} className="mt-1 border-navy/20" /></div>
              <div><Label>Région</Label><Input value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })} className="mt-1 border-navy/20" /></div>
              <div><Label>Type</Label>
                <Select value={f.property_type} onValueChange={(v) => setF({ ...f, property_type: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="libre">Libre</SelectItem><SelectItem value="occupee">Occupée</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Surface (m²)</Label><Input required type="number" value={f.surface} onChange={(e) => setF({ ...f, surface: e.target.value })} className="mt-1 border-navy/20" /></div>
              <div><Label>Pièces</Label><Input required type="number" value={f.rooms} onChange={(e) => setF({ ...f, rooms: e.target.value })} className="mt-1 border-navy/20" /></div>
              <div><Label>Prix ferme (€)</Label><Input required type="number" data-testid="ag-price" value={f.market_value} onChange={(e) => setF({ ...f, market_value: e.target.value })} className="mt-1 border-navy/20" /></div>
              <div><Label>Bouquet (€)</Label><Input required type="number" value={f.bouquet} onChange={(e) => setF({ ...f, bouquet: e.target.value })} className="mt-1 border-navy/20" /></div>
              <div><Label>Durée (années)</Label><Input required type="number" min={5} max={30} value={f.duration_years} onChange={(e) => setF({ ...f, duration_years: e.target.value })} className="mt-1 border-navy/20" /></div>
              <div><Label>DPE énergie</Label>
                <Select value={f.dpe_energy} onValueChange={(v) => setF({ ...f, dpe_energy: v })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{["A","B","C","D","E","F","G"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>DPE climat</Label>
                <Select value={f.dpe_climate} onValueChange={(v) => setF({ ...f, dpe_climate: v })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{["A","B","C","D","E","F","G"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Description</Label><Textarea required rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1 border-navy/20" /></div>
            </div>
            <div className="border-t border-navy/10 pt-4">
              <Label className="mb-2 block">Photos</Label>
              <ImageUploader value={photos} onChange={setPhotos} max={10} />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-navy/10">
              <button type="button" onClick={closeForm} className="px-5 py-2.5 border border-navy/20 text-sm font-bold text-navy hover:bg-navy/5">Annuler</button>
              <button type="submit" disabled={busy} data-testid="ag-save" className="px-6 py-2.5 bg-accent-brand text-white text-sm font-bold hover:bg-[#B08D22] disabled:opacity-60">
                {busy ? "Enregistrement..." : editing ? "Enregistrer les modifications" : "Envoyer pour modération"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
